import { useCallback, useState } from "react";
import { guessLocationFromTimeZone } from "@/lib/guessLocation";
import { clearShareParams, isValidPlace, readSharedPlace } from "@/lib/shareLink";

const STORAGE_KEY = "true-solar-time.place";

export type PlaceSource = "guess" | "saved" | "shared";

export interface Place {
  lat: number;
  lon: number;
  label: string;
  source: PlaceSource;
}

// Last place chosen in this browser (localStorage only; never sent anywhere).
function loadSaved(): Place | null {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    return isValidPlace(raw) ? { lat: raw.lat, lon: raw.lon, label: raw.label, source: "saved" } : null;
  } catch {
    return null;
  }
}

function save(place: Place) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat: place.lat, lon: place.lon, label: place.label }));
  } catch {
    // storage full or disabled: remembering is a nicety, not required
  }
}

function forgetSaved() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to forget
  }
}

function guessPlace(): Place {
  const g = guessLocationFromTimeZone();
  return { lat: g.latitude, lon: g.longitude, label: g.label, source: "guess" };
}

const ownPlace = () => loadSaved() ?? guessPlace();

// A shared link wins on load, but is never saved over the visitor's own place.
function initialPlace(): Place {
  const shared = readSharedPlace();
  return shared ? { ...shared, source: "shared" } : ownPlace();
}

export function usePlace() {
  const [place, setPlace] = useState<Place>(initialPlace);

  const setLocation = useCallback((lat: number, lon: number, label: string) => {
    const next: Place = { lat, lon, label, source: "saved" };
    save(next);
    clearShareParams();
    setPlace(next);
  }, []);

  const forget = useCallback(() => {
    forgetSaved();
    clearShareParams();
    setPlace(guessPlace());
  }, []);

  const leaveShared = useCallback(() => {
    clearShareParams();
    setPlace(ownPlace());
  }, []);

  return { place, setLocation, forget, leaveShared, initialLabel: place.label };
}
